import { useState } from 'react';
import type { TodayStoreRef } from '../utils/nearestStore';

interface ClockInLocationForkProps {
  /** The store the rep appears to be standing at, or null when unknown. */
  candidate: TodayStoreRef | null;
  onCheckInThere: (candidate: TodayStoreRef) => void;
  onHeadingOut: () => void;
  /** Punch the clock with no drive leg — the escape hatch for a rep the app
   * cannot locate who is not driving. A rep is never left unable to punch. */
  onClockInNoDrive: () => void;
  onClose: () => void;
}

/**
 * Clocking in confirms a status, it never assumes one.
 *
 * A drive leg on every clock-in is usually right — and silently wrong for
 * the rep who forgot to punch until they were standing in their first store,
 * whose "drive" then ran until someone cleaned it up. The app may NOTICE
 * where the rep is, but the status is theirs to declare: at a store → check
 * in there (the store page owns the clock-in and asks about the drive that
 * got them there); heading out → clock in and drive, said out loud.
 */
export default function ClockInLocationFork({
  candidate,
  onCheckInThere,
  onHeadingOut,
  onClockInNoDrive,
  onClose,
}: ClockInLocationForkProps) {
  const [showAtStoreHint, setShowAtStoreHint] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" data-testid="clockin-location-fork">
        {showAtStoreHint ? (
          <>
            <h3 className="text-lg font-semibold text-slate-900">Check in from the store</h3>
            <p className="mt-2 text-sm text-slate-600">
              Open the store you&rsquo;re at from your schedule and check in there — clocking in
              happens with the visit, and the store page will ask about the drive that got you
              there.
            </p>
            <button
              onClick={onClockInNoDrive}
              className="mt-4 w-full rounded-xl border border-slate-200 p-3 text-left hover:border-primary-300 hover:bg-primary-50"
            >
              <div className="font-medium text-slate-800">Just clock in — no drive</div>
              <div className="text-xs text-slate-500">
                Can&rsquo;t find the store on your schedule? Punch the clock now; nothing is invented
              </div>
            </button>
            <button
              onClick={onClose}
              className="mt-3 w-full rounded-xl bg-primary-600 py-3 font-medium text-white hover:bg-primary-700"
            >
              Got it
            </button>
          </>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-slate-900">
              {candidate ? `You're at ${candidate.name}` : 'Where are you starting?'}
            </h3>
            <div className="mt-4 space-y-2">
              {candidate && (
                <button
                  onClick={() => onCheckInThere(candidate)}
                  className="w-full rounded-xl border border-slate-200 p-3 text-left hover:border-primary-300 hover:bg-primary-50"
                >
                  <div className="font-medium text-slate-800">Check in at {candidate.name}</div>
                  <div className="text-xs text-slate-500">
                    Clock-in happens with the visit — no drive is recorded unless you add one
                  </div>
                </button>
              )}
              <button
                onClick={onHeadingOut}
                className="w-full rounded-xl border border-slate-200 p-3 text-left hover:border-primary-300 hover:bg-primary-50"
              >
                <div className="font-medium text-slate-800">I&rsquo;m heading out — clock in and drive</div>
                <div className="text-xs text-slate-500">Starts your day and the drive to your first store</div>
              </button>
              {!candidate && (
                <button
                  onClick={() => setShowAtStoreHint(true)}
                  className="w-full rounded-xl border border-slate-200 p-3 text-left hover:border-primary-300 hover:bg-primary-50"
                >
                  <div className="font-medium text-slate-800">I&rsquo;m already at a store</div>
                  <div className="text-xs text-slate-500">Check in from the store instead — no drive gets invented</div>
                </button>
              )}
            </div>
            <button onClick={onClose} className="mt-4 w-full py-2 text-sm text-slate-500">
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}
